/**
 * Tenant staff roster + invitations (WSRADM-0016).
 *
 * Bare `/admin/staff/**` paths so `resolveBaseUrl` uses `apiBase()` (`/api`).
 * Tenant is the JWT only — never a slug on the path, query, body, or header.
 * Bearer only; no cookies, no CSRF. Never saas-auth `/api/product-admin/**`
 * or `/internal/**`. Never `/admin/platform/**`.
 */
import { request } from '@/api/client';
import { ApiError } from '@/api/errors';
import { asId, asNumber, asText, isObject } from '@/api/parse';

export type AssignableRole = 'OWNER' | 'ADMIN' | 'STAFF';
export type SettableStatus = 'ACTIVE' | 'DISABLED';

export const ASSIGNABLE_ROLES: readonly AssignableRole[] = ['OWNER', 'ADMIN', 'STAFF'];

const STAFF_LADDER = new Set<string>(ASSIGNABLE_ROLES);

export interface StaffMember {
  userId: number;
  email: string;
  name: string | null;
  role: string;
  status: string;
  createdAt: string | null;
}

export interface StaffInvitation {
  invitationId: string;
  email: string | null;
  role: string;
  status: string;
  invitedByUserId: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export interface InviteStaffInput {
  email: string;
  role: AssignableRole;
  name?: string;
}

const STAFF_PATH = '/admin/staff';
const INVITATIONS_PATH = '/admin/staff/invitations';

function envelopeError(): never {
  throw new ApiError('UNKNOWN', 0, {});
}

function invitationPath(invitationId: string): string {
  return `${INVITATIONS_PATH}/${encodeURIComponent(invitationId)}`;
}

function memberRolePath(userId: number | string): string {
  return `${STAFF_PATH}/${encodeURIComponent(String(userId))}/role`;
}

function memberStatusPath(userId: number | string): string {
  return `${STAFF_PATH}/${encodeURIComponent(String(userId))}/status`;
}

export function parseStaffMember(raw: unknown): StaffMember | null {
  if (!isObject(raw) || Array.isArray(raw)) return null;
  const userId = asNumber(raw['userId']);
  if (userId === null) return null;
  return {
    userId,
    email: asText(raw['email']) ?? '',
    name: asText(raw['name']),
    role: asText(raw['role']) ?? '',
    status: asText(raw['status']) ?? '',
    createdAt: asText(raw['createdAt']),
  };
}

export function parseStaffInvitation(raw: unknown): StaffInvitation | null {
  if (!isObject(raw) || Array.isArray(raw)) return null;
  const invitationId = asId(raw['invitationId']);
  if (invitationId === null) return null;
  return {
    invitationId,
    email: asText(raw['email']),
    role: asText(raw['role']) ?? '',
    status: asText(raw['status']) ?? '',
    invitedByUserId: asId(raw['invitedByUserId']),
    createdAt: asText(raw['createdAt']),
    expiresAt: asText(raw['expiresAt']),
  };
}

function requireStaffMember(raw: unknown): StaffMember {
  const parsed = parseStaffMember(raw);
  if (parsed === null) envelopeError();
  return parsed;
}

function requireStaffInvitation(raw: unknown): StaffInvitation {
  const parsed = parseStaffInvitation(raw);
  if (parsed === null) envelopeError();
  return parsed;
}

export function isStaffLadderRole(role: string): role is AssignableRole {
  return STAFF_LADDER.has(role);
}

/** GET /admin/staff → `{ staff: StaffMember[] }`. Missing/non-array `staff` → UNKNOWN. */
export async function listStaff(): Promise<StaffMember[]> {
  const body = await request<unknown>('GET', STAFF_PATH);
  if (!isObject(body) || Array.isArray(body) || !Array.isArray(body['staff'])) {
    envelopeError();
  }
  const staff: StaffMember[] = [];
  for (const row of body['staff']) {
    const parsed = parseStaffMember(row);
    if (parsed !== null) staff.push(parsed);
  }
  return staff;
}

/**
 * GET /admin/staff/invitations?status=PENDING — JSON array only.
 * Client-side filter to OWNER|ADMIN|STAFF.
 */
export async function listStaffInvitations(): Promise<StaffInvitation[]> {
  const body = await request<unknown>('GET', INVITATIONS_PATH, {
    query: { status: 'PENDING' },
  });
  if (!Array.isArray(body)) envelopeError();
  const invitations: StaffInvitation[] = [];
  for (const row of body) {
    const parsed = parseStaffInvitation(row);
    if (parsed !== null && isStaffLadderRole(parsed.role)) invitations.push(parsed);
  }
  return invitations;
}

/** POST /admin/staff/invitations ← `{ email, role, name? }` only. */
export async function inviteStaff(input: InviteStaffInput): Promise<StaffInvitation> {
  const json: Record<string, unknown> = { email: input.email, role: input.role };
  if (input.name !== undefined) {
    const trimmed = input.name.trim();
    if (trimmed.length > 0) json['name'] = trimmed;
  }
  const body = await request<unknown>('POST', INVITATIONS_PATH, { json });
  return requireStaffInvitation(body);
}

/** DELETE /admin/staff/invitations/{invitationId} → 204. */
export async function cancelStaffInvitation(invitationId: string): Promise<void> {
  await request<unknown>('DELETE', invitationPath(invitationId));
}

/** POST /admin/staff/invitations/{invitationId}/resend → replace the row. */
export async function resendStaffInvitation(invitationId: string): Promise<StaffInvitation> {
  const body = await request<unknown>('POST', `${invitationPath(invitationId)}/resend`);
  return requireStaffInvitation(body);
}

/** PUT /admin/staff/{userId}/role ← `{ role }` → one StaffMember. */
export async function setStaffRole(
  userId: number | string,
  role: AssignableRole,
): Promise<StaffMember> {
  const body = await request<unknown>('PUT', memberRolePath(userId), { json: { role } });
  return requireStaffMember(body);
}

/** PUT /admin/staff/{userId}/status ← `{ status }` → one StaffMember. */
export async function setStaffStatus(
  userId: number | string,
  status: SettableStatus,
): Promise<StaffMember> {
  const body = await request<unknown>('PUT', memberStatusPath(userId), { json: { status } });
  return requireStaffMember(body);
}
